/**
 * Verdict de la section « Analyse du visage » du diagnostic — PUR. Sépare les deux causes
 * de lenteur : la caméra qui livre peu d'images (lumière), ou l'analyse qui coûte cher par
 * image (accélération graphique indisponible). Le démarrage de MediaPipe n'est pas compté.
 */
import { chooseDelegate, type Delegate, type DelegateMeasure } from '../detection/delegate';
import { BLINK_PROFILE } from '../detection/profiles';

/** Santé de la chaîne d'images (webcam) : par où passent les images, combien d'échecs. */
export interface AnalysisHealth {
  /** Où MediaPipe calcule. */
  readonly delegate: Delegate;
  /** MediaPipe dans un Web Worker (le fil principal reste libre). */
  readonly worker: boolean;
  /** rVFC : le navigateur prévient à chaque image ; rAF : relecture à chaque affichage. */
  readonly mode: 'rvfc' | 'raf';
  /** rVFC s'est tu alors que la vidéo jouait : passé sur rAF. */
  readonly stalled: boolean;
  /** Images dont l'analyse a levé une exception. */
  readonly errors: number;
  /** Rechargements de MediaPipe après des échecs consécutifs. */
  readonly restarts: number;
  /** Message du dernier échec ; `null` si aucun. */
  readonly lastError: string | null;
  /** Images reçues par la boucle, analyses lancées et terminées, analyses sans réponse. */
  readonly frames: number;
  readonly started: number;
  readonly completed: number;
  readonly timeouts: number;
  /** Vidéo cachée trouvée en pause et relancée. */
  readonly resumes: number;
  /** État de l'élément vidéo et de la piste caméra, en clair ; `null` si inconnu. */
  readonly video: string | null;
}

export interface AnalysisMeasure {
  /** Images livrées par la caméra (par seconde). */
  readonly cameraFps: number;
  /** Images analysées (par seconde), démarrage exclu. */
  readonly analysisFps: number;
  /** Durée moyenne d'une analyse, aller-retour compris (ms) ; `null` si inconnue. */
  readonly avgMs: number | null;
  /** Durée moyenne de l'inférence MediaPipe seule (ms) ; `null` si inconnue. */
  readonly inferenceMs: number | null;
  /** Part des images où un visage est vu. */
  readonly faceRatio: number;
  /** Du lancement à la première image analysée (chargement du modèle). */
  readonly warmupMs: number;
  /** Processeur graphique vu par le navigateur (WebGL) ; `null` si inconnu. */
  readonly renderer: string | null;
  /** `null` : source sans mesure (visage synthétique). */
  readonly health: AnalysisHealth | null;
}

/** Rendu graphique LOGICIEL : pas d'accélération matérielle (analyse 5 à 10 fois plus lente). */
export function isSoftwareRenderer(renderer: string): boolean {
  return /swiftshader|llvmpipe|softpipe|basic render|software/i.test(renderer);
}

export interface AnalysisVerdict {
  readonly level: 'ok' | 'warn' | 'error';
  readonly message: string;
  readonly detail: string;
}

const fmt = (n: number, digits = 0): string => n.toFixed(digits).replace('.', ',');

const delegateName = (d: Delegate): string => (d === 'GPU' ? 'GPU' : 'processeur');

function healthDetail(h: AnalysisHealth | null): string {
  if (!h) return '';
  const path = h.mode === 'rvfc' ? 'rVFC' : h.stalled ? 'rAF (rVFC muet)' : 'rAF';
  const restarts = h.restarts > 0 ? `, MediaPipe rechargé ${fmt(h.restarts)} fois` : '';
  const last = h.lastError ? ` ; dernière erreur : ${h.lastError}` : '';
  const errors = h.errors > 0 ? ` · échecs d’analyse : ${fmt(h.errors)}${restarts}${last}` : '';
  const where = h.worker ? ' (worker)' : ' (fil principal)';
  const timeouts = h.timeouts > 0 ? `, ${fmt(h.timeouts)} sans réponse` : '';
  const resumes = h.resumes > 0 ? ` · vidéo relancée ${fmt(h.resumes)} fois` : '';
  const loop = ` · boucle : ${fmt(h.frames)} images reçues, ${fmt(h.started)} analyses lancées, ${fmt(h.completed)} terminées${timeouts}${resumes}`;
  const video = h.video ? ` · vidéo : ${h.video}` : '';
  return ` · MediaPipe sur ${delegateName(h.delegate)}${where} · images via ${path}${errors}${loop}${video}`;
}

/** Une mesure par délégué, pour choisir. */
export interface DelegateComparison {
  readonly choice: Delegate;
  /** Phrase pour le diagnostic : les deux mesures et le choix mémorisé. */
  readonly text: string;
}

export function compareDelegates(measures: readonly DelegateMeasure[]): DelegateComparison {
  const choice = chooseDelegate(measures);
  const parts = measures.map((m) => {
    const cost = m.avgMs === null || m.analysisFps <= 0 ? '' : ` (${fmt(m.avgMs)} ms par image)`;
    const errors = m.errors > 0 ? `, ${fmt(m.errors)} échec${m.errors > 1 ? 's' : ''}` : '';
    return `${delegateName(m.delegate)} : ${fmt(m.analysisFps)} img/s${cost}${errors}`;
  });
  return {
    choice,
    text: `${parts.join(' · ')} → les jeux utiliseront le ${delegateName(choice)} sur cet appareil.`,
  };
}

const REPORT = 'recharge la page ; si ça se reproduit, signale-le avec ce détail.';
/** Part du temps occupée par l'analyse (durée × cadence) au-delà de laquelle elle est la limite. */
const BUSY_RATIO = 0.6;

export function analysisVerdict(
  m: AnalysisMeasure,
  minFps = BLINK_PROFILE.minFps,
): AnalysisVerdict {
  const inference = m.inferenceMs === null ? '' : `, dont inférence ${fmt(m.inferenceMs)} ms`;
  const cost = m.avgMs === null ? '' : ` (${fmt(m.avgMs)} ms par image${inference})`;
  const detail = `Caméra ${fmt(m.cameraFps)} img/s · analyse ${fmt(m.analysisFps)} img/s${cost} · démarrage ${fmt(m.warmupMs / 1000, 1)} s · GPU : ${m.renderer ?? 'inconnu'}${healthDetail(m.health)}`;
  if (m.analysisFps <= 0) {
    const cause =
      m.health && m.health.errors > 0
        ? `l’analyse a planté ${fmt(m.health.errors)} fois (carte graphique ?)`
        : 'les images de la caméra n’arrivent plus jusqu’à la page';
    return {
      level: 'error',
      message: `L’analyse s’est arrêtée après le démarrage : ${cause} — ${REPORT}`,
      detail,
    };
  }
  if (m.faceRatio < 0.5)
    return {
      level: 'warn',
      message: 'On ne voit pas bien ton visage : mets-toi face à la caméra, éclairé.',
      detail,
    };
  if (m.analysisFps >= minFps)
    return { level: 'ok', message: 'Visage suivi : les deux modes de jeu sont possibles.', detail };
  let cause: string;
  if (m.cameraFps < minFps)
    cause = `ta caméra ne fournit que ${fmt(m.cameraFps)} images par seconde : éclaire mieux ton visage (en lumière faible, elle ralentit).`;
  else if (m.renderer !== null && isSoftwareRenderer(m.renderer))
    cause =
      "l'accélération matérielle est désactivée (rendu graphique logiciel) : active-la dans les réglages du navigateur (Chrome : Paramètres → Système), puis redémarre-le.";
  else if (m.avgMs !== null && m.avgMs > 1000 / minFps)
    cause = m.health
      ? `l'analyse prend ${fmt(m.avgMs)} ms par image, même sur le meilleur des deux (${delegateName(m.health.delegate)}) : cet appareil est trop lent pour ce mode. Avec Brave, baisse les Shields pour ce site.`
      : `l'analyse prend ${fmt(m.avgMs)} ms par image : accélération graphique indisponible ? Avec Brave, baisse les Shields pour ce site ; sinon, active l'accélération matérielle dans les réglages du navigateur.`;
  else if (m.avgMs !== null && m.avgMs * m.analysisFps < 500)
    // Le calcul occupe moins de la moitié du temps : ce ne sont ni la caméra ni le calcul.
    cause = `les images n’arrivent pas assez vite jusqu’à l’analyse : ${REPORT}`;
  else if (m.avgMs !== null && m.avgMs * m.analysisFps >= BUSY_RATIO * 1000)
    // Une analyse à la fois : la cadence ne peut pas dépasser 1000 / durée d'une analyse.
    cause = `l'analyse prend ${fmt(m.avgMs)} ms par image et occupe tout le temps disponible : cet appareil est à la limite pour ce mode.`;
  else cause = 'l’appareil est occupé : ferme les autres onglets, désactive l’économie d’énergie.';
  return {
    level: 'warn',
    message: `Mode « le premier qui rit » OK ; trop lent pour « le premier qui cligne ». Cause probable : ${cause}`,
    detail,
  };
}
